import { z } from 'zod';
import type { QueueName } from './queues.js';
import { runDateSchema } from './run-date.js';

export type JobDefinition = {
  queue: QueueName;
  payload: z.ZodObject;
  attempts: number;
  backoff: { type: 'exponential'; delay: number };
};

export const campaignTotalsSweepPayload = z.strictObject({
  v: z.literal(1),
  runDate: runDateSchema.optional(),
});

export const campaignTotalsRecomputePayload = z.strictObject({
  v: z.literal(1),
  campaignPeriodId: z.uuid(),
  runDate: runDateSchema,
});

/**
 * Which of the two daily fetch schedules fired. It is part of the static
 * scheduler template, so the morning catch-up and the afternoon fetch of the
 * same day keep separate run keys in the ledger.
 */
export const EXCHANGE_RATE_FETCH_SLOTS = ['morning', 'afternoon'] as const;

export const exchangeRatesFetchPayload = z.strictObject({
  v: z.literal(1),
  slot: z.enum(EXCHANGE_RATE_FETCH_SLOTS),
  runDate: runDateSchema.optional(),
});

export const exchangeRatesBackfillPayload = z.strictObject({
  v: z.literal(1),
  requestId: z.uuid(),
});

/**
 * `dispatchKey` names the one dispatch that asked for this run — a fetch run's
 * key, or a fresh id from the operator route — so a retry of that dispatch is
 * deduplicated and a later one is not. The filters are scalars, never text.
 * `currency` is a code's shape only: this package depends on Zod alone, and the
 * job checks the code against `CURRENCIES` before it reads a row.
 */
export const donationBaseAmountsBackfillPayload = z.strictObject({
  v: z.literal(1),
  dispatchKey: z.string().regex(/^[a-z0-9-]{1,80}$/),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  donatedFrom: runDateSchema.optional(),
  donatedTo: runDateSchema.optional(),
});

/**
 * `trigger` says which dispatch asked: the import request itself, or the scan
 * finishing. The two have different job ids, so a scan that ends after the
 * request's job has spent its retries still gets the import processed; the
 * row's conditional `uploaded → processing` claim keeps the second from
 * running it twice.
 */
export const STATEMENT_IMPORT_PROCESS_TRIGGERS = ['request', 'scanned'] as const;

export const statementImportProcessPayload = z.strictObject({
  v: z.literal(1),
  statementImportId: z.uuid(),
  trigger: z.enum(STATEMENT_IMPORT_PROCESS_TRIGGERS),
});

/**
 * The same two dispatches for a donation import — docs/decisions/0104 §3:
 * the request, and the scan finishing. The row's conditional claim keeps the
 * second from processing it twice.
 */
export const DONATION_IMPORT_PROCESS_TRIGGERS = ['request', 'scanned'] as const;

export const donationImportProcessPayload = z.strictObject({
  v: z.literal(1),
  donationImportId: z.uuid(),
  trigger: z.enum(DONATION_IMPORT_PROCESS_TRIGGERS),
});

/**
 * A donation import's commit — docs/decisions/0104 §5. The import's own
 * `committing` row is the intent: who commits, which season and which
 * operator are read from it, never from the payload.
 */
export const donationImportCommitPayload = z.strictObject({
  v: z.literal(1),
  donationImportId: z.uuid(),
});

/**
 * A bulk change's preview and commit — docs/decisions/0125 §3–§5, the donation
 * import's two dispatches again: the request, and the scan finishing.
 */
export const BULK_CHANGE_PROCESS_TRIGGERS = ['request', 'scanned'] as const;

export const bulkChangeProcessPayload = z.strictObject({
  v: z.literal(1),
  bulkChangeId: z.uuid(),
  trigger: z.enum(BULK_CHANGE_PROCESS_TRIGGERS),
});

export const bulkChangeCommitPayload = z.strictObject({
  v: z.literal(1),
  bulkChangeId: z.uuid(),
});

export const attachmentScanPayload = z.strictObject({
  v: z.literal(1),
  attachmentId: z.uuid(),
});

/**
 * `dispatchKey` names what asked for this render — the issue, the void, or one
 * recording of a legal document number — so each re-render after the first has
 * a job id of its own rather than being deduplicated against a completed job.
 * The receipt row decides what is printed; the payload carries no text.
 */
export const receiptRenderPayload = z.strictObject({
  v: z.literal(1),
  receiptId: z.uuid(),
  dispatchKey: z.string().regex(/^[a-z0-9-]{1,80}$/),
});

/**
 * One inbound medium — its row decides what is fetched, from where, and what a
 * read resolves to; the payload names the row and nothing a sender wrote.
 */
export const inboundMediaFetchPayload = z.strictObject({
  v: z.literal(1),
  inboundMediaId: z.uuid(),
});

export const inboundMediaDecodePayload = z.strictObject({
  v: z.literal(1),
  inboundMediaId: z.uuid(),
});

/**
 * One `generated_files` row — an export of a list's whole filter, or a
 * printout. The row is the intent: it carries the kind, the requester and the
 * filter, so the payload names it and nothing a filter could be rewritten in.
 * There is a job name per kind so a failure, an alert and the run ledger say
 * which kind of file was not produced — docs/decisions/0043-the-worker.md §7.
 */
export const generatedFilePayload = z.strictObject({
  v: z.literal(1),
  generatedFileId: z.uuid(),
});

/** A daily retention run: the objects due on its run date are deleted, the rows kept. */
export const retentionRunPayload = z.strictObject({
  v: z.literal(1),
  runDate: runDateSchema.optional(),
});

/**
 * One raise of an operations alert. The row decides the code, the subject and
 * every value the message shows; the payload names the row and which raise of it
 * this is, so a re-alert of the same condition is a job of its own and a send
 * that a newer raise has overtaken does nothing.
 */
export const alertSendPayload = z.strictObject({
  v: z.literal(1),
  alertId: z.uuid(),
  raise: z.number().int().min(1),
});

/** A recurring pass with nothing to carry: it reads the clock, the ledger and the rates. */
export const recurringPassPayload = z.strictObject({
  v: z.literal(1),
});

/**
 * One due period of one pledge. The payload names the pledge and the day it
 * owes and nothing else: the row decides the amount, the campaign, the answers
 * and the mandate, so a payload somebody wrote into Redis can move no money it
 * was not already owed — docs/decisions/0043-the-worker.md §2.
 */
export const pledgeChargePayload = z.strictObject({
  v: z.literal(1),
  pledgeId: z.uuid(),
  dueDate: runDateSchema,
});

/** One `payment_mandates` row, read back from the provider or forgotten at it. */
export const paymentMandatePayload = z.strictObject({
  v: z.literal(1),
  paymentMandateId: z.uuid(),
});

/**
 * One `notifications` row. The row carries the rendered Turkish, the channel and
 * the destination, so the payload names the row and nothing a recipient could be
 * swapped for — docs/decisions/0043-the-worker.md §2 and
 * docs/plans/07-notifications-and-reporting.md §7.1.
 */
export const notificationDeliverPayload = z.strictObject({
  v: z.literal(1),
  notificationId: z.uuid(),
});

export const JOBS = {
  'campaign-totals.sweep': {
    queue: 'maintenance',
    payload: campaignTotalsSweepPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'campaign-totals.recompute': {
    queue: 'totals',
    payload: campaignTotalsRecomputePayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'exchange-rates.fetch': {
    queue: 'rates',
    payload: exchangeRatesFetchPayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 120_000 },
  },
  'exchange-rates.backfill': {
    queue: 'rates',
    payload: exchangeRatesBackfillPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'donation.base-amounts-backfill': {
    queue: 'maintenance',
    payload: donationBaseAmountsBackfillPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'statement-import.process': {
    queue: 'imports',
    payload: statementImportProcessPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /** A donation import's preview, judged row by row as entry judges — docs/decisions/0104 §4. */
  'donation-import.process': {
    queue: 'imports',
    payload: donationImportProcessPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /** A donation import's commit, one donation per valid row, one transaction each — docs/decisions/0104 §5. */
  'donation-import.commit': {
    queue: 'imports',
    payload: donationImportCommitPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /** A bulk change's preview, each row judged by the single-record path — docs/decisions/0125 §3. */
  'bulk-change.process': {
    queue: 'imports',
    payload: bulkChangeProcessPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /** A bulk change's commit, one create or update per row, one transaction each — docs/decisions/0125 §4. */
  'bulk-change.commit': {
    queue: 'imports',
    payload: bulkChangeCommitPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'attachment.scan': {
    queue: 'files',
    payload: attachmentScanPayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'receipt.render': {
    queue: 'files',
    payload: receiptRenderPayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'inbound-media.feedback': {
    queue: 'imports',
    payload: z.strictObject({ v: z.literal(1), receiptId: z.uuid() }),
    attempts: 5,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'inbound-media.fetch': {
    queue: 'imports',
    payload: inboundMediaFetchPayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'inbound-media.decode': {
    queue: 'imports',
    payload: inboundMediaDecodePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'inbound-media.expire': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'statement-import.expire': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * A donation import's raw file and its rows' captured cells and answers,
   * ninety days after it ends — committed, discarded or failed —
   * docs/decisions/0104, DONATION_IMPORT_RETENTION_DAYS in
   * packages/core/src/retention.ts.
   */
  'donation-import.expire': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * A payment slip's object, ten years from the end of the year its payment was
   * approved or 90 days after it was rejected or withdrawn — docs/decisions/0100,
   * PAYMENT_SLIP_RETENTION_YEARS in packages/core/src/retention.ts.
   */
  'payment-slip.expire': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * The three periods docs/decisions/0063 added, each on `maintenance` beside
   * the two 0049 built: a donation's own attachment, a `generated_files` row
   * whose bytes are long gone, and an audit payload past its ten years.
   */
  'donation-attachment.expire': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'generated-files.purge': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'audit-log.expire-payloads': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * A file a campaign form's own `file` field captured from a donor, two years
   * after it was uploaded — CAMPAIGN_FORM_FILE_RETENTION_YEARS in
   * packages/core/src/retention.ts.
   */
  'form-file.expire': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * The `outbox_redispatches` rows whose last ask is long past —
   * docs/decisions/0043-the-worker.md §4. The sweep writes one row per intent it
   * ever asks for and removes none, so without this the table only grows.
   */
  'outbox.purge': {
    queue: 'maintenance',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'donation.export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'reconciliation.shortfall-export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  /** The reconciliation history's whole filter, §5.11 — its own name, so a failure says which file was lost. */
  'reconciliation.history-export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'refund.export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'fund-transfer.export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'printout.render': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'document.queue-sheet': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /** §4.3's kesim report as an `.xlsx` — its own name, so a failure says which file was lost. */
  'donation.queue-export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  /** A season's donation import template, the campaign's form as an `.xlsx` — docs/decisions/0104 §2. */
  'donation.import-template': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  /** A bulk add-and-update template for users, institutions or donations — docs/decisions/0125 §2. */
  'bulk-change.template': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  /**
   * PRD §10's reports, each as the file of its own filter — one job name per
   * kind, so a failure, an alert and the run ledger say which report was not
   * produced (.claude/rules/file-uploads.md, *Generated files*).
   */
  'report.donation-totals-export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'report.campaign-performance-export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'report.donor-segments-export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  'report.season-comparison-export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  /** The trail a compliance request asks for — docs/plans/07-notifications-and-reporting.md §7.7. */
  'audit.export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  /**
   * Everything the organization holds about one donor, drawn as a document that
   * donor can read — §8.4's answer to KVKK article 11 and GDPR article 15. It
   * is a `files` job like every other export, and the row it names carries the
   * donor, so no donor id ever travels in a payload.
   */
  'donor.data-export': {
    queue: 'files',
    payload: generatedFilePayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 30_000 },
  },
  /**
   * The reporting read model's incremental pass —
   * docs/plans/07-notifications-and-reporting.md §7.5. **One attempt, and no
   * ledger row.** Each pass reads the watermark and rebuilds exactly the buckets
   * a change has made stale, so a pass missed is made up by the next one and
   * retrying a failed one inside the same five minutes would only contend for
   * the claim it already holds. It sits on `totals` beside the campaign
   * recompute, which is the other thing that adds donations up.
   */
  'report-rollup.refresh': {
    queue: 'totals',
    payload: recurringPassPayload,
    attempts: 1,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'generated-files.expire': {
    queue: 'maintenance',
    payload: recurringPassPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * Marks a `payment_links` row whose time has run out `expired`, for the rows
   * nobody ever asks about again. `PaymentLinkWrites.draw` already expires a
   * live link when the next one is drawn for the same subject, but a link
   * nobody re-draws stays `active` past its `expires_at` for ever — and, by the
   * partial unique index, keeps occupying the one-live-link slot
   * (docs/plans/06-portal-and-field.md §6.2 block B).
   */
  'payment-link.expire': {
    queue: 'maintenance',
    payload: recurringPassPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * Keeps the *Operasyon Sorumlusu* label on `user_roles` agreeing with the
   * campaign staff assignments for the changes no write makes — a window
   * starting or ending — and grants every missing label on its first pass
   * after migration `0126` (docs/decisions/0096). The authority itself never
   * waits for it: 0090's grant asks the window on every request.
   */
  'campaign-staff.role-sync': {
    queue: 'maintenance',
    payload: recurringPassPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'alert.send': {
    queue: 'maintenance',
    payload: alertSendPayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'provider-movements.sync': {
    queue: 'imports',
    payload: recurringPassPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * Freezes the TRY, USD and EUR equivalents of movements booked before a
   * bulletin was stored for their day — docs/decisions/0126. The selection
   * (`try_equivalent_minor IS NULL`) is its own guard, so a pass missed is made
   * up by the next one.
   */
  'account-transaction.equivalents-fill': {
    queue: 'maintenance',
    payload: recurringPassPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'payment-credentials.reseal': {
    queue: 'maintenance',
    payload: recurringPassPayload,
    attempts: 1,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'scheduled-jobs.watchdog': {
    queue: 'maintenance',
    payload: recurringPassPayload,
    attempts: 1,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  'outbox.sweep': {
    queue: 'maintenance',
    payload: recurringPassPayload,
    attempts: 1,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * **E-mail is at least once.** A duplicate is an annoyance, a lost receipt
   * notice is not, so the job retries and the row's conditional claim is what
   * stops two deliveries of one row — docs/decisions/0043-the-worker.md's
   * catalogue.
   */
  'notification.deliver-email': {
    queue: 'notify-email',
    payload: notificationDeliverPayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * **At most once, on purpose.** A second SMS is a second bill and a complaint,
   * and nothing in a retry can tell a message that was delivered from one that
   * was not. The row moves to `sending` before the provider is called, so a job
   * that finds it there marks it `delivery_unknown` and never calls again.
   */
  'notification.deliver-sms': {
    queue: 'notify-sms',
    payload: notificationDeliverPayload,
    attempts: 1,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /** At most once, for the same reason as SMS: a WhatsApp template message is billed. */
  'notification.deliver-whatsapp': {
    queue: 'notify-whatsapp',
    payload: notificationDeliverPayload,
    attempts: 1,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /** At most once: a push provider deduplicates nothing, and a repeated banner reads as a second event. */
  'notification.deliver-push': {
    queue: 'notify-push',
    payload: notificationDeliverPayload,
    attempts: 1,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * The daily pass over what pledges owe. It only reads and adds children, so a
   * period whose charge fails retries alone and no other donor is charged again
   * because of it.
   */
  'payment.pledge-sweep': {
    queue: 'payments',
    payload: retentionRunPayload,
    attempts: 3,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * **One attempt in BullMQ, on purpose.** Whether a declined charge is tried
   * again is the domain's policy, written on `pledge_charges.attempt_count` and
   * `next_attempt_at` and asked by the sweep — not on a queue's backoff, which
   * would ask a bank the same question five times in ten minutes and get the
   * merchant blocked. The row is committed before the provider is called and
   * the call carries `pledge-<id>-<dueDate>`, so even a redelivery is the same
   * payment — docs/decisions/0043-the-worker.md's catalogue.
   */
  'payment.charge-pledge': {
    queue: 'payments',
    payload: pledgeChargePayload,
    attempts: 1,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * Reads back what the donor's first, hosted payment stored at the provider —
   * the saved method's opaque handle and the brand and last four a donor
   * recognises. It is a provider read rather than part of the callback because
   * no transaction makes a network call, and the mandate row is its own outbox
   * intent while it is `authorizing`.
   */
  'payment.record-mandate': {
    queue: 'payments',
    payload: paymentMandatePayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 60_000 },
  },
  /**
   * Tells the provider to forget a mandate a donor or an operator cancelled.
   * It is belt and braces rather than the gate: nothing charges a cancelled
   * pledge, because the sweep reads BTSis's own status and never the
   * provider's. The row is the intent while `revoke_requested_at` is set and
   * `revoked_at` is not.
   */
  'payment.revoke-mandate': {
    queue: 'payments',
    payload: paymentMandatePayload,
    attempts: 5,
    backoff: { type: 'exponential', delay: 60_000 },
  },
} as const satisfies Record<string, JobDefinition>;

/**
 * How long BullMQ holds a job between its first attempt starting and its last
 * one starting, from the exponential backoff the job is added with: `delay`
 * after the first failure, twice that after the second, and so on. The
 * attempts' own running time, and any wait for a free worker, come on top.
 */
export function retryScheduleMs(definition: Pick<JobDefinition, 'attempts' | 'backoff'>): number {
  return definition.backoff.delay * (2 ** (definition.attempts - 1) - 1);
}

export type JobName = keyof typeof JOBS;

export const JOB_NAMES = Object.keys(JOBS) as JobName[];

export type JobPayload<Name extends JobName> = z.infer<(typeof JOBS)[Name]['payload']>;

export function isJobName(name: string): name is JobName {
  return Object.hasOwn(JOBS, name);
}
